// Server-driven practice sessions. The client shows what the server serves and submits what the user did;
// selection, order, grading, re-insertion and counting all happen on the server.
// Contract: platform/docs/api-v1/practice.md
import type { Call } from './call'
import type { Question } from './index'

export type PracticeMode = 'learn' | 'consolidate' | 'assessment' | 'random'

export type PracticeIntent =
  | { kind: 'chapter'; subject: string; chapter?: string; mode?: PracticeMode; count?: number; minutes?: number; startQuestionId?: string; resume?: boolean; reviewRepeat?: ReviewRepeat }
  | { kind: 'due_review'; subject?: string; limit?: number; reviewRepeat?: ReviewRepeat }
  | { kind: 'wrong_book'; subject?: string; limit?: number }
  | { kind: 'random'; subject: string; chapters?: string[]; year?: number; count?: number; weakFirst?: boolean }
  | { kind: 'exam'; subject: string; chapters?: string[]; year?: number; count?: number; minutes?: number }
  | { kind: 'selector'; selector: string }

export interface ReviewRepeat { enabled: boolean; count: number }

export interface PracticeSession {
  id: string
  kind: string
  mode: PracticeMode | null
  status: 'open' | 'finished' | 'expired'
  ruleVersion: string
  total: number
  answered: number
  startPosition: number
  deadlineAt: string | null
  reviewRepeat: ReviewRepeat
  intent?: { kind?: string; subject?: string; chapter?: string | null; mode?: PracticeMode; [key: string]: unknown }
  startedAt?: string
  finishedAt?: string | null
  report?: PracticeReport
}

export interface PracticeItem {
  itemId: string
  position: number
  occurrence: number
  reason: string
  reasonDetail?: Record<string, unknown>
  question: Question
  priorState?: { attempts: number; lastCorrect: boolean | null; locked: boolean }
  previousAnswer?: { selected: number[]; correct: boolean } | null
}

export interface PracticeSessionResponse {
  session: PracticeSession
  items: PracticeItem[]
  resume?: { position: number }
  created?: boolean
  resumed?: boolean
  shortfall?: unknown
  report?: PracticeReport
}

export interface QueueEntry { itemId: string; position: number; questionId: string; reason: string; occurrence: number }

export interface PracticeCounts { dueCount: number; wrongCount: number; todayAttempts: number }

export interface PracticeAnswerInput {
  clientAttemptId: string
  itemId: string
  selectedOptionIds: number[]
  msSpent?: number
  answeredAt?: string
  reanswer?: boolean
}

export interface PracticeAnswerResult {
  itemId: string
  correct?: boolean
  answer?: number[]
  explanation?: string | null
  optionExplanations?: Record<string, string> | null
  outcome?: string
  saved?: boolean
  state?: { attempts: number; mastered: boolean; remediationStage: number; dueAtSeq: number | null; dueAt: string | null; everWrong: boolean } | null
  session: { answered: number; total: number; status: string }
  upcoming?: { inserted: PracticeItem[]; removed: string[]; queue: QueueEntry[] }
  counts?: PracticeCounts
}

export interface PracticeBatchResult {
  results: ({ ok: true } & PracticeAnswerResult | { ok: false; error: string; itemId?: string })[]
  session: PracticeSession
  upcoming?: { queue: QueueEntry[] }
  counts?: PracticeCounts
}

export interface PracticeReport {
  total: number
  answered: number
  correct: number
  unanswered: number
  pct: number
  band: string
  byChapter: { subject: string; chapter: string; total: number; correct: number }[]
  newlyMastered: number
  newlyWrong: number
  scheduledReviews: number
  usedSeconds: number
  wrongItemIds: string[]
}

interface Figure { value: number | null; [key: string]: unknown }
export interface PracticeHome {
  ruleVersion: string
  answerSeq: number
  dueCount: Figure
  wrongCount: Figure
  todayAttempts: Figure
  streakDays: Figure
  stablePercent: Figure
  done: Figure & { total: number }
  accuracy: Figure
  rank: { level: number; name: string }
}

export type ResumeCandidate = PracticeSession

export function createPracticeApi(call: Call) {
  return {
    home: () => call<PracticeHome>({ method: 'GET', path: '/api/v1/practice/home' }),
    createSession: (body: { clientSessionId: string; surface: string; clientVersion: string; window?: number; intent: PracticeIntent }) =>
      call<PracticeSessionResponse>({ method: 'POST', path: '/api/v1/practice/sessions', body }),
    getSession: (id: string, window?: number) =>
      call<PracticeSessionResponse>({ method: 'GET', path: `/api/v1/practice/sessions/${encodeURIComponent(id)}`, query: { window } }),
    /** "Continue practising" candidates: open sessions, optionally for one scope. */
    openSessions: (scope: { kind?: string; subject?: string; chapter?: string; limit?: number } = {}) =>
      call<{ sessions: ResumeCandidate[] }>({ method: 'GET', path: '/api/v1/practice/sessions', query: { status: 'open', ...scope } }),
    answer: (sessionId: string, body: PracticeAnswerInput) =>
      call<PracticeAnswerResult>({ method: 'POST', path: `/api/v1/practice/sessions/${encodeURIComponent(sessionId)}/answers`, body }),
    answerBatch: (sessionId: string, answers: PracticeAnswerInput[]) =>
      call<PracticeBatchResult>({ method: 'POST', path: `/api/v1/practice/sessions/${encodeURIComponent(sessionId)}/answers/batch`, body: { answers } }),
    finish: (sessionId: string) =>
      call<{ report: PracticeReport; session: PracticeSession }>({ method: 'POST', path: `/api/v1/practice/sessions/${encodeURIComponent(sessionId)}/finish`, body: {} }),
  }
}
